import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../../../Services/AppContext';
import AddressWhisperer, { AddressConfig } from './AddressWhisperer';

interface AddressWhispererNetteProps {
	/** HTML name for the street input (main field) */
	inputName: string;
	/** Form field key for the main field (e.g. "street") - used to update React state on select */
	fieldKey: string;
	fieldNames: Record<string, string>;
	responseMapping: Record<string, string>;
	apiEndpoint: string;
	queryParamName?: string;
	value?: string;
	placeholder?: string;
	label?: string;
	disabled?: boolean;
	required?: boolean;
	netteRules?: string;
	hasInvalid?: boolean;
}

/**
 * AddressWhisperer wrapper for Nette forms.
 * Updates sibling form inputs in the DOM when an address is selected.
 */
const AddressWhispererNette: React.FC<AddressWhispererNetteProps> = ({
	inputName,
	fieldKey,
	fieldNames,
	responseMapping,
	apiEndpoint,
	queryParamName = 'q',
	value = '',
	placeholder,
	label,
	disabled,
	netteRules,
	hasInvalid = false,
}) => {
	const { translation } = useAppContext();
	const containerRef = useRef<HTMLDivElement>(null);
	const [streetValue, setStreetValue] = useState(value);

	useEffect(() => {
		setStreetValue(value);
	}, [value]);

	const onAddressSelect = useCallback(
		(item: Record<string, unknown>) => {
			const form = containerRef.current?.closest('form');
			const apiKey = responseMapping[fieldKey];
			const streetVal =
				apiKey != null && item[apiKey] != null
					? String(item[apiKey])
					: ((item.fullAddress ??
							item.label ??
							item.displayName ??
							'') as string);
			setStreetValue(streetVal);

			Object.entries(responseMapping).forEach(([formField, apiKeyEntry]) => {
				const fieldVal = item[apiKeyEntry];
				if (fieldVal == null) return;
				const name = fieldNames[formField];
				if (!name || !form) return;
				const input =
					(form.elements.namedItem(name) as HTMLInputElement | null) ??
					Array.from(form.elements).find(
						(el): el is HTMLInputElement =>
							el instanceof HTMLInputElement &&
							(el as HTMLInputElement).name === name,
					) ??
					null;
				if (input) {
					input.value = String(fieldVal);
					input.dispatchEvent(new Event('input', { bubbles: true }));
					input.dispatchEvent(new Event('change', { bubbles: true }));
				}
			});

			// ensure street input has value (may be rendered by React)
			const streetInputName = fieldNames[fieldKey];
			if (streetInputName && form && streetVal) {
				const streetInput =
					(form.elements.namedItem(
						streetInputName,
					) as HTMLInputElement | null) ??
					Array.from(form.elements).find(
						(el): el is HTMLInputElement =>
							el instanceof HTMLInputElement &&
							(el as HTMLInputElement).name === streetInputName,
					) ??
					null;
				if (streetInput) {
					streetInput.value = streetVal;
					streetInput.dispatchEvent(new Event('input', { bubbles: true }));
					streetInput.dispatchEvent(new Event('change', { bubbles: true }));
				}
			}
		},
		[fieldNames, responseMapping, fieldKey],
	);

	const addressConfig: AddressConfig = {
		apiEndpoint,
		queryParamName,
		responseMapping,
		onAddressSelect,
	};

	const resolvedPlaceholder =
		(placeholder && translation?.[placeholder]) || placeholder;

	return (
		<div ref={containerRef}>
			<AddressWhisperer
				addressConfig={addressConfig}
				value={streetValue}
				onChange={setStreetValue}
				placeholder={resolvedPlaceholder}
				label={label}
				disabled={disabled}
				name={inputName}
				netteRules={netteRules}
				hasInvalid={hasInvalid}
			/>
		</div>
	);
};

export default AddressWhispererNette;
